import type { CSSProperties, ReactNode } from 'react';

type SocialFollowProps = {
    platformName: string;
    profileUrl: string;
    profileName: string;
    handle?: string;
    title: string;
    description: string;
    buttonLabel: string;
    followerCount?: string;
    className?: string;
    darkMode?: boolean;
    accentColor: string;
    openInNewTab?: boolean;
    logo: ReactNode;
    buttonLogo: ReactNode;
    graphic: ReactNode;
};

const SocialFollow = ({
    platformName,
    profileUrl,
    profileName,
    handle,
    title,
    description,
    buttonLabel,
    followerCount,
    className = '',
    darkMode = false,
    accentColor,
    openInNewTab = true,
    logo,
    buttonLogo,
    graphic,
}: SocialFollowProps) => {
    const containerClasses = darkMode
        ? 'border-slate-700 bg-slate-950 text-white'
        : 'border-slate-200 bg-white text-slate-950';

    const mutedTextClasses = darkMode ? 'text-slate-300' : 'text-slate-600';
    const metaTextClasses = darkMode ? 'text-slate-400' : 'text-slate-500';

    return (
        <section
            className={`relative overflow-hidden rounded-lg border shadow-sm ${containerClasses} ${className}`}
        >
            <div className="relative grid gap-6 p-6 md:grid-cols-[minmax(0,1fr)_280px] md:items-center md:p-8">
                <div className="flex min-w-0 flex-col gap-5">
                    <div className="flex items-center gap-3">
                        {logo}
                        <div className="min-w-0">
                            <p
                                className={`text-sm font-medium uppercase tracking-normal ${metaTextClasses}`}
                            >
                                {platformName}
                            </p>
                            <p className="truncate text-base font-semibold">{profileName}</p>
                        </div>
                    </div>

                    <div className="max-w-2xl">
                        <h2 className="text-2xl font-semibold leading-tight md:text-3xl">
                            {title}
                        </h2>
                        <p className={`mt-3 text-sm leading-6 md:text-base ${mutedTextClasses}`}>
                            {description}
                        </p>
                    </div>

                    {(handle || followerCount) && (
                        <div className={`flex flex-wrap gap-3 text-sm ${metaTextClasses}`}>
                            {handle && <span>@{handle}</span>}
                            {followerCount && <span>{followerCount}</span>}
                        </div>
                    )}

                    <div>
                        <a
                            href={profileUrl}
                            target={openInNewTab ? '_blank' : undefined}
                            rel={openInNewTab ? 'noopener noreferrer' : undefined}
                            aria-label={`${buttonLabel} - ${profileName}`}
                            className="inline-flex max-w-full items-center gap-2 rounded-md px-5 py-3 text-sm font-semibold text-white transition-opacity hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-offset-2"
                            style={
                                {
                                    backgroundColor: accentColor,
                                    '--tw-ring-color': accentColor,
                                } as CSSProperties
                            }
                        >
                            {buttonLogo}
                            <span className="whitespace-normal text-left">{buttonLabel}</span>
                        </a>
                    </div>
                </div>

                <div className="min-h-[210px]">{graphic}</div>
            </div>
        </section>
    );
};

export default SocialFollow;
